import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_BUSINESS_URL } from '@/utils';

import {
  BranchSettings,
  BranchSettingsApi,
  CreateBranchSettingsPayload,
  UpdateBranchSettingsPayload,
  BranchSettingsFilters,
} from '../models';
import { mapBranchSettingsApiToModel } from '../adapters';

const branchSettingsService = createApi({
  reducerPath: 'branchSettingsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: ['BranchSetting', 'BranchSettings'] as const,
  endpoints: (builder) => ({
    getBranchSettings: builder.query<
      PaginatedResponse<BranchSettings>,
      {
        branchId: string;
        paginated: PaginatedRequest;
        filters?: BranchSettingsFilters;
      }
    >({
      query: ({ branchId, paginated, filters }) => {
        const params: Record<string, any> = {
          branchId,
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        };

        if (filters?.key && filters.key.trim() !== '') {
          params.key = filters.key.trim();
        }
        if (filters?.value && filters.value.trim() !== '') {
          params.value = filters.value.trim();
        }

        return {
          url: '/branch-settings',
          params,
        };
      },
      transformResponse: (response: PaginatedResponse<BranchSettingsApi>) => ({
        ...response,
        items: response.items.map(mapBranchSettingsApiToModel),
      }),
      providesTags: ['BranchSettings'],
    }),
    getBranchSettingById: builder.query<
      BranchSettings,
      { settingId: string; branchId: string }
    >({
      query: ({ settingId }) => ({
        url: `/branch-settings/${settingId}`,
      }),
      transformResponse: (response: BranchSettingsApi) =>
        mapBranchSettingsApiToModel(response),
      providesTags: ['BranchSetting', 'BranchSettings'],
    }),
    createBranchSetting: builder.mutation<
      BranchSettings,
      CreateBranchSettingsPayload
    >({
      query: (body) => ({
        url: '/branch-settings',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BranchSettings'],
    }),
    updateBranchSetting: builder.mutation<
      BranchSettings,
      { settingId: string; branchId: string; data: UpdateBranchSettingsPayload }
    >({
      query: ({ settingId, branchId, data }) => ({
        url: `/branch-settings/${settingId}`,
        method: 'PUT',
        body: {
          ...data,
          branchId,
        },
      }),
      invalidatesTags: ['BranchSetting', 'BranchSettings'],
    }),
    deleteBranchSetting: builder.mutation<void, string>({
      query: (settingId) => ({
        url: `/branch-settings/${settingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['BranchSetting', 'BranchSettings'],
    }),
  }),
});

export default branchSettingsService;
export const {
  useGetBranchSettingsQuery,
  useGetBranchSettingByIdQuery,
  useCreateBranchSettingMutation,
  useUpdateBranchSettingMutation,
  useDeleteBranchSettingMutation,
} = branchSettingsService;
